import React, { useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useStoreValue, storeSet, storeGet } from '../../../../store';
import { useCueSheetHistory } from '../../../../store/cuesheetTemporal';
import {
    enrichCueSheetItems,
    tracksToCueSheetUpdates,
    getNextPriorityForLayer,
} from '../../../../helpers/cuesheetMigration';
import MyStudioTimeline from '../../../../components/TimelineEditor/MyStudioTimeline';
import { EMPTY_SELECTION, SELECTION_KINDS, getSelectedIds } from '../../../../helpers/selection';
import '../../../../assets/components/StudioTimeline.css';

const StudioTimeline = () => {
    // 1. Get projectId from route params
    const { projectId } = useParams();

    // 2. Subscribe to store values
    const cueSheetTimelineItems = useStoreValue('cueSheet');
    const cachedAudioUrls = useStoreValue('cachedAudioUrls');
    const cachedVideoUrl = useStoreValue('cachedVideoUrl');
    const projectVideos = useStoreValue('projectVideos');
    const selectedVideo = useStoreValue('selectedVideo');
    const audioCacheLoading = useStoreValue('audioCacheLoading');
    const cueSheetLoading = useStoreValue('cueSheetLoading');
    const isLoading = cueSheetLoading || audioCacheLoading;
    const aiNoteSubmissions = useStoreValue('aiNoteSubmissions');
    const activeAiNoteHighlight = useStoreValue('activeAiNoteHighlight');

    // 3. Video URL selection (memoized)
    const selectedVideoUrl = useMemo(() => {
        if (cachedVideoUrl) return cachedVideoUrl;
        const chosen = projectVideos.find(video => video.id === selectedVideo);
        return chosen?.url || null;
    }, [cachedVideoUrl, projectVideos, selectedVideo]);

    const selectedVideoThumbnail = useMemo(() => {
        const chosen = projectVideos.find(video => video.id === selectedVideo);
        return chosen?.thumbnailUrl || null;
    }, [projectVideos, selectedVideo]);

    // Smart Sync: tagli di scena (secondi) del video selezionato -> marker sulla timeline
    const syncMarkers = useMemo(() => {
        const chosen = projectVideos.find(video => video.id === selectedVideo);
        const cuts = chosen?.syncPoints?.cuts;
        return Array.isArray(cuts) ? cuts : [];
    }, [projectVideos, selectedVideo]);

    // 4. Undo/redo history for cueSheet
    const { undo, redo, canUndo, canRedo, undoLabel, redoLabel } = useCueSheetHistory();

    // Tracks are enriched cue sheet items, so a selection on the timeline is a
    // selection of cue items: the Comm Center scopes its feed to it. The store is
    // written to, never subscribed to, so selecting elsewhere does not re-render
    // the timeline.
    const selection = useStoreValue('selection');
    const selectedCueIds = useMemo(
        () => getSelectedIds(selection, SELECTION_KINDS.CUE),
        [selection]
    );

    const handleSelectionChange = useCallback((ids) => {
        storeSet('selection', ids.length
            ? { kind: SELECTION_KINDS.CUE, ids, anchor: ids[ids.length - 1] }
            : EMPTY_SELECTION);
    }, []);

    // Ref to prevent handleTracksChange from double-saving when a cut fires
    const cutInProgressRef = useRef(false);

    // 5. Fixed layers configuration for sb_editor mode
    const layersData = {
        REFERENCE: {
            color: 'var(--layer-reference)',
            bgColor: 'var(--layer-reference-bg)'
        },
        MUSIC: {
            color: 'var(--layer-music)',
            bgColor: 'var(--layer-music-bg)'
        },
        SFX: {
            color: 'var(--layer-sfx)',
            bgColor: 'var(--layer-sfx-bg)'
        },
        MIX: {
            color: 'var(--layer-dialogue)',
            bgColor: 'var(--layer-dialogue-bg)'
        }
    };

    // 6. Enrich cuesheet items with computed timeline fields (memoized)
    const tracks = useMemo(
        () => enrichCueSheetItems(cueSheetTimelineItems, cachedAudioUrls),
        [cueSheetTimelineItems, cachedAudioUrls]
    );
    //console.log('tracks', tracks)

    // 7. Handle cut operation — optimistic cuesheet update + backend call
    const handleCut = useCallback(({ originalItem, firstCutDuration, secondCutStart, secondCutDuration, secondCutStartPlayback, newId }) => {
        cutInProgressRef.current = true;
        storeSet('cutCueSheetItem', projectId, originalItem, firstCutDuration, secondCutStart, secondCutDuration, secondCutStartPlayback, newId);
        setTimeout(() => { cutInProgressRef.current = false; }, 0);
    }, [projectId]);

    // 8. Handle video upload from FileuploadDropZone placeholder
    const handleVideoUpload = useCallback(async (files) => {
        const file = files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        await storeSet('createVideoItem', projectId, formData);
    }, [projectId]);

    // Handle drop from left panel list items onto the timeline
    const handleExternalDrop = useCallback((item, timecode) => {
        storeSet('saveCueSheetUpdates', projectId, [{ id: item.id, timecode }]);
    }, [projectId]);

    const handleLayerSelection = useCallback(({ layer, startPos, endPos }) => {
        storeSet('stemSplitterSelection', { layer, startPos, endPos, timelineType: 'studio' });
    }, []);

    // A Pen marker toggles its drawing on the canvas; Pin / Mood highlight their range.
    const handleAiNoteHighlight = useCallback((id) => {
        const submission = id && (storeGet('aiNoteSubmissions') || []).find(s => s.id === id);
        if (submission?.mode === 'penai') {
            storeSet('activeAiNoteHighlight', id);
            storeSet('penaiViewData', submission.canvasJson ? { id, canvasJson: submission.canvasJson } : null);
            return;
        }
        storeSet('activeAiNoteHighlight', id);
        storeSet('penaiViewData', null);
    }, []);

    const handleAiNoteSelection = useCallback((data) => {
        if (!data) {
            storeSet('aiNoteSelection', null);
            return;
        }
        const mode = storeGet('timelineEditingMode');
        storeSet('aiNoteSelection', { ...data, timelineType: 'studio', mode });
    }, []);

    // 9. Handle track changes from TimelineEditor
    const handleTracksChange = useCallback((newTracks) => {
        if (cutInProgressRef.current) return; // cut action handles its own cuesheet updates
        // Assign priority to newly added tracks (present in newTracks but not in original)
        const originalIds = new Set(cueSheetTimelineItems.map(item => item.id));
        const tracksWithPriority = newTracks.map(track => {
            if (!originalIds.has(track.id)) {
                return {
                    ...track,
                    priority: getNextPriorityForLayer(newTracks, track.layer),
                };
            }
            return track;
        });

        const updates = tracksToCueSheetUpdates(tracksWithPriority, cueSheetTimelineItems, 24);

        // Detect tracks removed from the timeline → set timecode: null
        const newTrackIds = new Set(newTracks.map(t => t.id));
        const removedUpdates = cueSheetTimelineItems
            .filter(item => item.timecode != null && !newTrackIds.has(item.id))
            .map(item => ({ id: item.id, timecode: null }));

        const allUpdates = [...updates, ...removedUpdates];

        if (projectId && allUpdates.length > 0) {
            storeSet('saveCueSheetUpdates', projectId, allUpdates);
        }
    }, [cueSheetTimelineItems, projectId]);

    // 10. Render MyStudioTimeline (handles store-connected volumes + playback)
    return (
        <div className="studioTimeline">
            {isLoading && (
                <div className="studioTimeline__loadingOverlay">
                    <div className="studioTimeline__spinner" />
                </div>
            )}
            <MyStudioTimeline
                mode="sb_editor"
                videoUrl={selectedVideoUrl}
                videoPoster={selectedVideoThumbnail}
                tracks={tracks}
                layersData={layersData}
                onTracksChange={handleTracksChange}
                onCut={handleCut}
                onSelectionChange={handleSelectionChange}
                selectedIds={selectedCueIds}
                onVideoUpload={handleVideoUpload}
                onExternalDrop={handleExternalDrop}
                onLayerSelectionComplete={handleLayerSelection}
                onAiNoteSelectionComplete={handleAiNoteSelection}
                aiNoteSubmissions={aiNoteSubmissions}
                syncMarkers={syncMarkers}
                activeAiNoteHighlight={activeAiNoteHighlight}
                onAiNoteHighlight={handleAiNoteHighlight}
                undo={undo}
                redo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
                undoLabel={undoLabel}
                redoLabel={redoLabel}
            />
        </div>
    );
};

export default StudioTimeline;
